import { Clock, MapPin } from 'lucide-react';
import { StatusChip, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { usePractitionerPractices } from '@/settings/services';
import type {
  PracticeList,
  WorkingHourSummary,
} from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Practitioner Practices tab — PROD-763 (read-only).
 *
 * Flutter parity: `doctor_practices_tab.dart`. List of practices this
 * practitioner is assigned to, rendered as cards with:
 *   - Practice logo (or fallback initial)
 *   - Practice name + "Primary" badge when `isPrimary === true`
 *   - Address (with location glyph)
 *   - Working-hours summary, grouped by identical time window
 *     (e.g. "Mon-Fri from 09:00 to 17:00" + "Sat from 09:00 to 13:00")
 *
 * Flutter has NO add / edit / remove path (line 142 of the .dart file
 * carries `// TODO Implement logic for add and Edit` that was never
 * finished). We mirror that scope — read-only display, no mutations.
 *
 * Wire-shape note (verified against the existing `usePracticesList`
 * envelope, the practitioner endpoint reuses `PracticeList[]`):
 *   - `weedayId` (typo'd — not `weekdayId`) on `WorkingHourSummary`.
 *   - `startWeekDay` / `endWeekDay` are full weekday names; we slice
 *     to 3 chars for display (`"Monday"` → `"Mon"`).
 */
export interface PractitionerPracticesTabProps {
  doctorSetupId: string;
}

export function PractitionerPracticesTab({
  doctorSetupId,
}: PractitionerPracticesTabProps) {
  const { data, isLoading, error } = usePractitionerPractices(doctorSetupId);

  if (isLoading) {
    return (
      <BrandLoading size={48} label="Loading practices…" />
    );
  }

  if (error) {
    return (
      <p className="text-sm text-status-error">
        Failed to load practices. Please try again.
      </p>
    );
  }

  const practices = data ?? [];

  if (practices.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No practices assigned to this practitioner.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className={SETTINGS_SECTION_TITLE}>
        Practices
      </h2>
      <div className="space-y-3">
        {practices.map((practice) => (
          <PracticeCard
            key={practice.practiceSetupId ?? practice.practiceName ?? ''}
            practice={practice}
          />
        ))}
      </div>
    </div>
  );
}

function PracticeCard({ practice }: { practice: PracticeList }) {
  const initial =
    practice.practiceName?.trim().slice(0, 1).toUpperCase() ?? '?';

  return (
    <section data-slot="settings-record" data-record-size="lg" className="rounded-lg border border-border-card bg-surface-card shadow-1 px-4 py-4">
      <div className="flex items-start gap-4">
        <div data-slot="settings-practice-photo" className="flex h-18 w-18 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100">
          {practice.practiceImageURL ? (
            <img
              src={practice.practiceImageURL}
              alt={`${practice.practiceName ?? 'Practice'} logo`}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-size-monogram font-semibold text-blue-700">
              {initial}
            </span>
          )}
        </div>

        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <h3 data-slot="settings-record-title" className="text-md font-bold text-text-default">
              {practice.practiceName || '—'}
            </h3>
            {practice.isPrimary && (
              <StatusChip tone="soft" semantic="info">
                Primary
              </StatusChip>
            )}
          </div>

          <div className="flex items-start gap-2 text-sm text-text-default">
            <MapPin
              className="size-4 shrink-0 mt-1 text-text-secondary"
              aria-hidden
            />
            <span>{practice.address || '—'}</span>
          </div>

          <WorkingHoursSummary entries={practice.workingHours ?? []} />
        </div>
      </div>
    </section>
  );
}

function WorkingHoursSummary({
  entries,
}: {
  entries: WorkingHourSummary[];
}) {
  if (entries.length === 0) {
    return (
      <div className="flex items-start gap-2 text-sm text-text-default">
        <Clock
          className="size-4 shrink-0 mt-1 text-text-secondary"
          aria-hidden
        />
        <span>n/a</span>
      </div>
    );
  }

  const groups = groupByTimeWindow(entries);

  return (
    <div className="flex items-start gap-2 text-sm text-text-default">
      <Clock
        className="size-4 shrink-0 mt-1 text-text-secondary"
        aria-hidden
      />
      <div className="flex-1 space-y-1">
        {groups.map(({ timeWindow, items }) => (
          <p key={timeWindow}>
            <span className="font-semibold">{summarizeWeekdays(items)}</span>{' '}
            {timeWindow}
          </p>
        ))}
      </div>
    </div>
  );
}

/**
 * Group working-hour entries by their `from {start} to {end}` window.
 * Matches Flutter `groupBy` keyed on the same display string
 * (doctor_practices_tab.dart:57-61). Preserves insertion order so the
 * "primary" window (whichever appears first in the response) renders
 * at the top.
 */
function groupByTimeWindow(
  entries: WorkingHourSummary[],
): Array<{ timeWindow: string; items: WorkingHourSummary[] }> {
  const map = new Map<string, WorkingHourSummary[]>();
  for (const entry of entries) {
    const key = `from ${entry.startTime ?? ''} to ${entry.endTime ?? ''}`;
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      map.set(key, [entry]);
    }
  }
  return Array.from(map.entries()).map(([timeWindow, items]) => ({
    timeWindow,
    items,
  }));
}

/**
 * Compress a list of weekday entries (each carrying `weedayId` + 3-letter
 * day name) into a display string like `Mon-Wed, Fri`. Cleanroom port
 * of Flutter `workTimeSummary` (utils.dart:688-746) — same output for
 * any input, but iterative range-builder reads more cleanly in TS.
 *
 * Behaviour:
 *   - All 7 days   → `"Mon-Sun"`
 *   - Single entry → `"Mon"` (or `"Mon-Tue"` if start != end on the row)
 *   - Otherwise    → consecutive `weedayId` groups joined with `-`,
 *                    discontiguous groups joined with `, `
 */
function summarizeWeekdays(entries: WorkingHourSummary[]): string {
  if (entries.length === 0) return '';

  if (entries.length === 7) {
    const first = three(entries[0]?.startWeekDay);
    const last = three(entries[6]?.endWeekDay);
    return `${first}-${last}`;
  }

  if (entries.length === 1) {
    const e = entries[0];
    if (!e) return '';
    const start = three(e.startWeekDay);
    const end = three(e.endWeekDay);
    return start === end ? start : `${start}-${end}`;
  }

  const sorted = [...entries].sort(
    (a, b) => (a.weedayId ?? 0) - (b.weedayId ?? 0),
  );

  const ranges: string[] = [];
  let runStart = sorted[0]!;
  let runEnd = sorted[0]!;

  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i]!;
    if ((curr.weedayId ?? 0) === (runEnd.weedayId ?? 0) + 1) {
      runEnd = curr;
      continue;
    }
    ranges.push(formatRange(runStart, runEnd));
    runStart = curr;
    runEnd = curr;
  }
  ranges.push(formatRange(runStart, runEnd));

  return ranges.join(', ');
}

function formatRange(
  start: WorkingHourSummary,
  end: WorkingHourSummary,
): string {
  const s = three(start.startWeekDay);
  if (start === end) return s;
  const e = three(end.startWeekDay);
  return `${s}-${e}`;
}

function three(weekday: string | null | undefined): string {
  return (weekday ?? '').slice(0, 3);
}
